import { useDataProvider } from "@/providers/DataProvider";
import Button from "react-bootstrap/Button";
import ListGroup from "react-bootstrap/ListGroup";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import Category from "@/models/Category";

export const CategoryListDialog = () => {
  const { categories, updateCategory, addCategory, deleteCategory } =
    useDataProvider();
  const navigate = useNavigate();
  const onHide = () => navigate(-1);

  // State for editing categories
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [adding, setAdding] = useState(false);
  const [addName, setAddName] = useState("");
  const [addDescription, setAddDescription] = useState("");

  const startEdit = (category: Category) => {
    setEditingId(category.id);
    setEditName(category.name);
    setEditDescription(category.description || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditDescription("");
  };

  const saveEdit = (category: Category) => {
    updateCategory(category.setName(editName).setDescription(editDescription));
    setEditingId(null);
    setEditName("");
    setEditDescription("");
  };

  const handleDelete = (category: Category) => {
    if (
      window.confirm(
        `Are you sure you want to delete the category "${category.name}"?`,
      )
    ) {
      deleteCategory(category.id);
    }
  };

  const startAdd = () => {
    setAdding(true);
    setAddName("");
    setAddDescription("");
  };

  const cancelAdd = () => {
    setAdding(false);
    setAddName("");
    setAddDescription("");
  };

  const saveAdd = () => {
    if (addName.trim() === "") return;
    addCategory(new Category({ name: addName, description: addDescription }));
    setAdding(false);
    setAddName("");
    setAddDescription("");
  };

  return (
    <Modal show={true} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Category List</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ListGroup>
          {categories && categories.length > 0 ? (
            categories.map((category: Category) => (
              <ListGroup.Item key={category.id}>
                {editingId === category.id ? (
                  <>
                    <Form.Group className="mb-2">
                      <Form.Label visuallyHidden>Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Category Name"
                        autoFocus
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label visuallyHidden>Description</Form.Label>
                      <Form.Control
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Description"
                      />
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => saveEdit(category)}
                        disabled={editName.trim() === ""}
                      >
                        Save
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={cancelEdit}
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{category.name}</strong>
                      <div>{category.description}</div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => startEdit(category)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(category)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </ListGroup.Item>
            ))
          ) : (
            <div>No categories available.</div>
          )}
        </ListGroup>
        <div className="mt-3">
          {adding ? (
            <div>
              <Form.Group className="mb-2">
                <Form.Label visuallyHidden>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  placeholder="Category Name"
                  autoFocus
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label visuallyHidden>Description</Form.Label>
                <Form.Control
                  type="text"
                  value={addDescription}
                  onChange={(e) => setAddDescription(e.target.value)}
                  placeholder="Description"
                />
              </Form.Group>
              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={saveAdd}
                  disabled={addName.trim() === ""}
                >
                  Add
                </Button>
                <Button variant="secondary" size="sm" onClick={cancelAdd}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="success" size="sm" onClick={startAdd}>
              + Add Category
            </Button>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
